import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Banknote,
  Bus,
  Check,
  Contact,
  GraduationCap,
  Loader2,
  User,
  UtensilsCrossed,
} from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useSchoolContext } from '@/contexts/SchoolContext'
import { studentService } from '@/services/student.service'
import { schoolManagementService } from '@/services/school-management.service'
import { schoolModulesService, type FeeStructure, type TransportRoute } from '@/services/school-modules.service'
import type { ClassRoom } from '@/types/school-management'
import { StudentIDCard } from '@/components/student/StudentIDCard'
import { ParentAccessPanel } from '@/components/students/ParentAccessPanel'
import { sortClasses } from '@/utils/classOrder'

/**
 * Enrol a pupil in one go: profile, the school's own class, its fee grid,
 * a bus route, a canteen credit, then the ID card and the pupil's login.
 * Every step writes through the real APIs when the admin confirms.
 * Opened from anywhere with window.dispatchEvent(new Event('admin:open-enrol-student')).
 */

const draftKey = 'appacademia:admin:enrol-student-draft'

type Draft = {
  firstName: string
  lastName: string
  gender: string
  dateOfBirth: string
  guardianPhone: string
  guardianEmail: string
  classroomId: string
  feeStructureId: string
  routeId: string
  stopId: string
  cafeteriaCredit: string
}

const emptyDraft: Draft = {
  firstName: '',
  lastName: '',
  gender: '',
  dateOfBirth: '',
  guardianPhone: '',
  guardianEmail: '',
  classroomId: '',
  feeStructureId: '',
  routeId: '',
  stopId: '',
  cafeteriaCredit: '',
}

type Enrolled = { studentId: string; login: string; password: string | null; problems: string[] }

const formatXAF = (n: number) => `${new Intl.NumberFormat('fr-FR').format(n)} FCFA`

export function EnrolStudentWizard() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { currentSchool } = useSchoolContext()
  const schoolId = currentSchool?.id
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [classrooms, setClassrooms] = useState<ClassRoom[]>([])
  const [feeGrids, setFeeGrids] = useState<FeeStructure[]>([])
  const [routes, setRoutes] = useState<TransportRoute[]>([])
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [enrolled, setEnrolled] = useState<Enrolled | null>(null)

  const steps = [
    { id: 'profile', label: t('enrolWizard.steps.profile', 'Profile'), icon: User },
    { id: 'class', label: t('enrolWizard.steps.class', 'Class'), icon: GraduationCap },
    { id: 'fees', label: t('enrolWizard.steps.fees', 'Fees'), icon: Banknote },
    { id: 'transport', label: t('enrolWizard.steps.transport', 'Transport'), icon: Bus },
    { id: 'cafeteria', label: t('enrolWizard.steps.cafeteria', 'Canteen'), icon: UtensilsCrossed },
    { id: 'id-card', label: t('enrolWizard.steps.idCard', 'ID card'), icon: Contact },
  ]

  useEffect(() => {
    const openWizard = () => {
      try {
        const saved = localStorage.getItem(draftKey)
        setDraft(saved ? { ...emptyDraft, ...JSON.parse(saved) } : emptyDraft)
      } catch {
        setDraft(emptyDraft)
      }
      setStep(0)
      setEnrolled(null)
      setOpen(true)
    }
    window.addEventListener('admin:open-enrol-student', openWizard)
    return () => window.removeEventListener('admin:open-enrol-student', openWizard)
  }, [])

  // The school's own classes, fee grids and bus routes
  useEffect(() => {
    if (!open) return
    schoolManagementService.getClassrooms().then(rooms => setClassrooms(sortClasses(rooms, r => r.name || r.grade_level))).catch(() => setClassrooms([]))
    schoolModulesService.getFeeStructures(schoolId).then(grids => setFeeGrids(grids.filter(g => g.is_active))).catch(() => setFeeGrids([]))
    schoolModulesService.getFeeCategories(schoolId)
      .then(cats => setCategoryNames(Object.fromEntries(cats.map(c => [c.id, c.name]))))
      .catch(() => setCategoryNames({}))
    if (schoolId) schoolModulesService.getTransportRoutes(schoolId).then(setRoutes).catch(() => setRoutes([]))
  }, [open, schoolId])

  const classroom = classrooms.find(c => c.id === draft.classroomId)
  const gridsForClass = useMemo(
    () => feeGrids.filter(g => !g.grade_level || g.grade_level === classroom?.grade_level),
    [feeGrids, classroom],
  )
  const grid = feeGrids.find(g => g.id === draft.feeStructureId)
  const route = routes.find(r => r.id === draft.routeId)
  const fullName = `${draft.firstName} ${draft.lastName}`.trim()
  const progress = Math.round(((step + 1) / steps.length) * 100)

  const updateDraft = (field: keyof Draft, value: string) => {
    setDraft(current => {
      const next = { ...current, [field]: value }
      if (field === 'classroomId') {
        // the class's own fee grid is proposed; the admin can change it
        const room = classrooms.find(c => c.id === value)
        const own = feeGrids.find(g => g.grade_level === room?.grade_level) ?? feeGrids.find(g => !g.grade_level)
        next.feeStructureId = own?.id ?? ''
      }
      if (field === 'routeId') next.stopId = ''
      return next
    })
  }

  const canContinue = step === 0 ? !!draft.firstName.trim() && !!draft.lastName.trim()
    : step === 1 ? !!draft.classroomId
    : true

  const saveDraft = () => {
    try { localStorage.setItem(draftKey, JSON.stringify(draft)) } catch { /* private mode */ }
    toast({ title: t('enrolWizard.draftSaved', 'Enrolment draft saved'), description: t('enrolWizard.draftSavedDesc', 'You can resume this enrolment later.') })
    setOpen(false)
  }

  const enrol = async () => {
    if (!classroom) return
    setSaving(true)
    const problems: string[] = []
    try {
      const created = await studentService.createStudent({
        full_name: fullName,
        grade_level: classroom.grade_level,
        gender: draft.gender || undefined,
        date_of_birth: draft.dateOfBirth || undefined,
        parent_emails: draft.guardianEmail.trim() ? [draft.guardianEmail.trim()] : undefined,
        profile: draft.guardianPhone.trim() ? { emergency_contact: draft.guardianPhone.trim() } : undefined,
      })
      try {
        await schoolManagementService.addStudentToClassroom(classroom.id, created.id)
      } catch {
        problems.push(t('enrolWizard.problem.class', 'Not placed in {{name}}: add the pupil to the class from the class list.', { name: classroom.name }))
      }
      if (grid) {
        try {
          await schoolModulesService.assignFeeToStudent({
            student_id: created.user_id, fee_structure_id: grid.id, total_amount: grid.amount,
            due_date: grid.due_date ?? undefined,
          })
        } catch {
          problems.push(t('enrolWizard.problem.fees', 'Fees not assigned: assign them from Finances.'))
        }
      }
      if (route) {
        try {
          await schoolModulesService.enrollStudentTransport({
            school_id: schoolId, student_id: created.user_id, route_id: route.id,
            stop_id: draft.stopId || undefined, direction: 'both',
          } as any)
        } catch {
          problems.push(t('enrolWizard.problem.transport', 'Not added to {{route}}: add the pupil from Transport.', { route: route.route_name }))
        }
      }
      const credit = parseFloat(draft.cafeteriaCredit)
      if (credit > 0) {
        try {
          await schoolModulesService.topUpCafeteriaCredit(created.id, credit, t('enrolWizard.creditNote', 'Credit at enrolment'))
        } catch {
          problems.push(t('enrolWizard.problem.canteen', 'Canteen credit not added: top up from Canteen.'))
        }
      }
      try { localStorage.removeItem(draftKey) } catch { /* private mode */ }
      setEnrolled({ studentId: created.id, login: created.login || created.email, password: created.temporary_password ?? null, problems })
      window.dispatchEvent(new Event('admin:student-enrolled'))
    } catch (e: any) {
      toast({
        title: t('enrolWizard.failed', 'The pupil was not enrolled'),
        description: e?.response?.data?.detail || t('enrolWizard.failedDesc', 'Check the details and try again.'),
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const close = () => {
    setOpen(false)
    setStep(0)
    setDraft(emptyDraft)
    setEnrolled(null)
  }

  const choice = (selected: boolean) => cn(
    'flex w-full items-center justify-between rounded-xl border p-4 text-left transition-all',
    selected ? 'border-[#0B4DBA] bg-[#EAF1FC] text-[#073584]' : 'border-slate-200 bg-white hover:border-slate-300',
  )

  const renderStep = () => {
    if (step === 0) {
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="first-name">{t('enrolWizard.firstName', 'First name')}</Label>
            <Input id="first-name" value={draft.firstName} onChange={e => updateDraft('firstName', e.target.value)} placeholder="Amina" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="last-name">{t('enrolWizard.lastName', 'Last name')}</Label>
            <Input id="last-name" value={draft.lastName} onChange={e => updateDraft('lastName', e.target.value)} placeholder="Bakary" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gender">{t('enrolWizard.gender', 'Sex')}</Label>
            <select id="gender" value={draft.gender} onChange={e => updateDraft('gender', e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              <option value="">—</option>
              <option value="F">{t('enrolWizard.girl', 'Girl')}</option>
              <option value="M">{t('enrolWizard.boy', 'Boy')}</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dob">{t('enrolWizard.dateOfBirth', 'Date of birth')}</Label>
            <Input id="dob" type="date" value={draft.dateOfBirth} onChange={e => updateDraft('dateOfBirth', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="guardian-phone">{t('enrolWizard.guardianPhone', 'Guardian phone')}</Label>
            <Input id="guardian-phone" value={draft.guardianPhone} onChange={e => updateDraft('guardianPhone', e.target.value)} placeholder="+237 6 99 00 00 00" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="guardian-email">{t('enrolWizard.guardianEmail', 'Guardian e-mail')}</Label>
            <Input id="guardian-email" type="email" value={draft.guardianEmail} onChange={e => updateDraft('guardianEmail', e.target.value)} placeholder="parent@email.com" />
          </div>
        </div>
      )
    }

    if (step === 1) {
      if (!classrooms.length) {
        return <p className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500">
          {t('enrolWizard.noClasses', 'Create your classes first (School management > Configuration), then enrol pupils.')}
        </p>
      }
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          {classrooms.map(room => (
            <button key={room.id} type="button" onClick={() => updateDraft('classroomId', room.id)} className={choice(draft.classroomId === room.id)}>
              <span>
                <span className="block font-semibold">{room.name}</span>
                <span className="text-sm text-slate-500">
                  {t('enrolWizard.pupilsInClass', '{{count}} pupils', { count: room.student_count ?? 0 })}
                </span>
              </span>
              {draft.classroomId === room.id && <Check className="h-5 w-5 text-[#0B4DBA]" />}
            </button>
          ))}
        </div>
      )
    }

    if (step === 2) {
      return (
        <div className="space-y-3">
          {gridsForClass.length === 0 && (
            <p className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              {t('enrolWizard.noFeeGrid', 'No fee grid for {{grade}} yet. Create one in Finances; you can assign the fees later.', { grade: classroom?.grade_level ?? '' })}
            </p>
          )}
          {gridsForClass.map(g => (
            <button key={g.id} type="button" onClick={() => updateDraft('feeStructureId', g.id)} className={choice(draft.feeStructureId === g.id)}>
              <span>
                <span className="block font-semibold">{categoryNames[g.category_id] ?? t('enrolWizard.fees', 'Fees')} — {formatXAF(g.amount)}</span>
                <span className="text-sm text-slate-500">
                  {g.grade_level ?? t('enrolWizard.allLevels', 'All levels')}
                  {g.installments_allowed > 1 && ` · ${t('enrolWizard.installments', '{{n}} instalments', { n: g.installments_allowed })}`}
                  {g.due_date && ` · ${t('enrolWizard.due', 'due {{date}}', { date: new Date(g.due_date).toLocaleDateString() })}`}
                </span>
              </span>
              {draft.feeStructureId === g.id && <Check className="h-5 w-5 text-[#0B4DBA]" />}
            </button>
          ))}
          <button type="button" onClick={() => updateDraft('feeStructureId', '')} className={choice(!draft.feeStructureId)}>
            <span className="font-medium">{t('enrolWizard.noFeesNow', 'Assign the fees later')}</span>
            {!draft.feeStructureId && <Check className="h-5 w-5 text-[#0B4DBA]" />}
          </button>
        </div>
      )
    }

    if (step === 3) {
      return (
        <div className="space-y-3">
          <button type="button" onClick={() => updateDraft('routeId', '')} className={choice(!draft.routeId)}>
            <span className="font-medium">{t('enrolWizard.noTransport', 'No school bus')}</span>
            {!draft.routeId && <Check className="h-5 w-5 text-[#0B4DBA]" />}
          </button>
          {routes.map(r => (
            <button key={r.id} type="button" onClick={() => updateDraft('routeId', r.id)} className={choice(draft.routeId === r.id)}>
              <span>
                <span className="block font-semibold">{r.route_name}</span>
                <span className="text-sm text-slate-500">{t('enrolWizard.stops', '{{count}} stops', { count: r.stops?.length ?? 0 })}</span>
              </span>
              {draft.routeId === r.id && <Check className="h-5 w-5 text-[#0B4DBA]" />}
            </button>
          ))}
          {route && (route.stops?.length ?? 0) > 0 && (
            <div className="space-y-2">
              <Label htmlFor="stop">{t('enrolWizard.stop', 'Stop')}</Label>
              <select id="stop" value={draft.stopId} onChange={e => updateDraft('stopId', e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                <option value="">{t('enrolWizard.anyStop', 'Choose later')}</option>
                {route.stops.map((s: any) => <option key={s.id} value={s.id}>{s.stop_name ?? s.name}</option>)}
              </select>
            </div>
          )}
          {!routes.length && (
            <p className="text-sm text-slate-500">{t('enrolWizard.noRoutes', 'No bus route yet: routes are created in Transport.')}</p>
          )}
        </div>
      )
    }

    if (step === 4) {
      return (
        <div className="max-w-sm space-y-2">
          <Label htmlFor="credit">{t('enrolWizard.canteenCredit', 'Canteen credit to add now (FCFA)')}</Label>
          <Input id="credit" type="number" min={0} step={500} value={draft.cafeteriaCredit}
            onChange={e => updateDraft('cafeteriaCredit', e.target.value)} placeholder="0" />
          <p className="text-sm text-slate-500">{t('enrolWizard.canteenHelp', 'Leave empty if the pupil does not eat at the canteen. Parents can top up later.')}</p>
        </div>
      )
    }

    if (enrolled) {
      return (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4 text-sm">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
              <p className="font-semibold">{t('enrolWizard.done', '{{name}} is enrolled in {{class}}.', { name: fullName, class: classroom?.name })}</p>
            </div>
            <dl className="grid gap-2 rounded-xl border border-slate-200 p-4">
              <div className="flex justify-between gap-3"><dt>{t('enrolWizard.login', 'Login')}</dt><dd className="font-mono font-medium">{enrolled.login}</dd></div>
              {enrolled.password && (
                <div className="flex justify-between gap-3"><dt>{t('enrolWizard.password', 'Password')}</dt><dd className="font-mono font-medium">{enrolled.password}</dd></div>
              )}
            </dl>
            {enrolled.password && (
              <p className="text-slate-500">{t('enrolWizard.passwordOnce', 'This password is shown once: write it down or print the card for the family.')}</p>
            )}
            {enrolled.problems.map(p => <p key={p} className="rounded-lg bg-amber-50 p-3 text-amber-800">{p}</p>)}
            <div className="rounded-xl border border-slate-200 p-4">
              <h4 className="mb-3 font-semibold text-[#13225B]">{t('parentAccess.title', 'Parent access')}</h4>
              <ParentAccessPanel studentId={enrolled.studentId} linkedEmail={draft.guardianEmail.trim() || undefined} />
            </div>
          </div>
          <StudentIDCard studentId={enrolled.studentId} />
        </div>
      )
    }

    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
        <h3 className="mb-3 font-display text-lg font-semibold text-[#13225B]">{t('enrolWizard.summary', 'Check and enrol')}</h3>
        <dl className="grid gap-2">
          <div className="flex justify-between gap-3"><dt>{t('enrolWizard.pupil', 'Pupil')}</dt><dd className="font-medium text-slate-900">{fullName}</dd></div>
          <div className="flex justify-between gap-3"><dt>{t('enrolWizard.steps.class', 'Class')}</dt><dd className="font-medium text-slate-900">{classroom?.name ?? '—'}</dd></div>
          <div className="flex justify-between gap-3"><dt>{t('enrolWizard.steps.fees', 'Fees')}</dt><dd className="font-medium text-slate-900">{grid ? formatXAF(grid.amount) : t('enrolWizard.later', 'later')}</dd></div>
          <div className="flex justify-between gap-3"><dt>{t('enrolWizard.steps.transport', 'Transport')}</dt><dd className="font-medium text-slate-900">{route?.route_name ?? t('enrolWizard.noTransport', 'No school bus')}</dd></div>
          <div className="flex justify-between gap-3"><dt>{t('enrolWizard.steps.cafeteria', 'Canteen')}</dt><dd className="font-medium text-slate-900">{parseFloat(draft.cafeteriaCredit) > 0 ? formatXAF(parseFloat(draft.cafeteriaCredit)) : '—'}</dd></div>
        </dl>
        <p className="mt-4">{t('enrolWizard.cardAfter', 'The ID card and the pupil\'s login appear as soon as the pupil is enrolled.')}</p>
      </div>
    )
  }

  const last = step === steps.length - 1

  return (
    <Dialog open={open} onOpenChange={value => (value ? setOpen(true) : close())}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-hidden rounded-2xl border-slate-200 p-0 shadow-[0_24px_60px_rgba(19,34,91,.24)]">
        <DialogHeader className="border-b border-slate-200 px-6 py-5 text-left">
          <DialogTitle className="font-display text-2xl text-[#13225B]">{t('enrolWizard.title', 'Enrol a pupil')}</DialogTitle>
          <DialogDescription>{t('enrolWizard.subtitle', 'Class, fees, transport, canteen and ID card in one go.')}</DialogDescription>
        </DialogHeader>

        <div className="grid min-h-[520px] overflow-hidden lg:grid-cols-[230px_1fr]">
          <aside className="border-r border-slate-200 bg-slate-50 p-5">
            <div className="mb-5 h-2 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full rounded-full bg-[#0B4DBA] transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="space-y-2">
              {steps.map((item, index) => {
                const Icon = item.icon
                const isCurrent = index === step
                const isDone = index < step || (!!enrolled && index <= step)
                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={!!enrolled || (index > step && !canContinue) || (index > 1 && !draft.classroomId)}
                    onClick={() => setStep(index)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed',
                      isCurrent ? 'bg-[#EAF1FC] font-semibold text-[#073584]' : 'text-slate-600 hover:bg-white',
                    )}
                  >
                    <span className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full',
                      isDone ? 'bg-emerald-100 text-emerald-700' : isCurrent ? 'bg-white text-[#0B4DBA]' : 'bg-white text-slate-400',
                    )}>
                      {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </span>
                    {item.label}
                  </button>
                )
              })}
            </div>
          </aside>

          <section className="overflow-y-auto p-6">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t('enrolWizard.stepOf', 'Step {{n}} of {{total}}', { n: step + 1, total: steps.length })}
              </p>
              <h2 className="font-display text-xl font-semibold text-[#13225B]">{steps[step].label}</h2>
            </div>
            {renderStep()}
          </section>
        </div>

        <DialogFooter className="border-t border-slate-200 px-6 py-4 sm:justify-between sm:space-x-0">
          {enrolled ? <span /> : (
            <Button type="button" variant="ghost" onClick={saveDraft}>{t('enrolWizard.saveLater', 'Save & finish later')}</Button>
          )}
          <div className="flex gap-2">
            {enrolled ? (
              <Button type="button" className="bg-[#0B4DBA] hover:bg-[#0941A0]" onClick={close}>{t('enrolWizard.close', 'Done')}</Button>
            ) : (
              <>
                <Button type="button" variant="outline" disabled={step === 0 || saving} onClick={() => setStep(s => Math.max(0, s - 1))}>
                  {t('enrolWizard.back', 'Back')}
                </Button>
                <Button type="button" className="bg-[#0B4DBA] hover:bg-[#0941A0]" disabled={!canContinue || saving || (last && !classroom)}
                  onClick={() => (last ? enrol() : setStep(s => s + 1))}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {last ? t('enrolWizard.enrol', 'Enrol and create the ID card') : t('enrolWizard.continue', 'Continue')}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
